import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "../../../components/Snackbar";
import { RENAME_INTEGRATION_MUTATION } from "../../../apis/salesforce/queries";
import { EDITABLE_INTEGRATION_TITLE_TEXTS } from "../consts";
import { useSalesforceIntegrationDetailsContext } from "../contexts/useSalesforceIntegrationDetailsContext";
import { HG_FORCE_CLIENT } from "../../../utils/consts";

// Edit mode component
const EditingTitle: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  disabled: boolean;
  initialWidth?: number;
  validation: {
    isTooShort: boolean;
  };
}> = ({
  value,
  onChange,
  onSave,
  onCancel,
  disabled,
  initialWidth,
  validation,
}) => {
  const [currentWidth, setCurrentWidth] = useState(initialWidth || 600);

  useEffect(() => {
    // Animate to full width after component mounts
    if (initialWidth && initialWidth < 600) {
      const timer = setTimeout(() => {
        setCurrentWidth(600);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [initialWidth]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    },
    [onSave, onCancel]
  );

  const helperText = useMemo(() => {
    if (validation.isTooShort) {
      return EDITABLE_INTEGRATION_TITLE_TEXTS.validation.tooShort(
        EDITABLE_INTEGRATION_TITLE_TEXTS.validation.minNonWhitespaceChars
      );
    }

    return "";
  }, [validation]);

  return (
    <ClickAwayListener onClickAway={onSave}>
      <Box sx={{ display: "inline-block" }}>
        <TextField
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="standard"
          size="small"
          autoFocus
          disabled={disabled}
          error={validation.isTooShort}
          helperText={helperText}
          sx={(theme) => ({
            width: currentWidth,
            maxWidth: 600,
            transition:
              "width 0.5s cubic-bezier(0.4, 0, 0.2, 1), all 0.5s ease-in-out",
            "& .MuiInputBase-root": {
              height: "auto",
              lineHeight: theme.typography.h4.lineHeight,
            },
            "& .MuiInputBase-input": {
              ...theme.typography.h4,
              padding: "2px 4px",
              backgroundColor: "transparent",
              height: "auto",
              "&:focus": {
                outline: "none",
              },
            },
            "& .MuiFormHelperText-root": {
              position: "absolute",
              top: "100%",
              left: 0,
              mt: 0.5,
              fontSize: 10,
              whiteSpace: "nowrap",
            },
          })}
        />
      </Box>
    </ClickAwayListener>
  );
};
// Display mode component - Google Docs style with tooltip
const DisplayTitle = React.forwardRef<
  HTMLDivElement,
  { name: string; onEdit: () => void }
>(({ name, onEdit }, ref) => {
  return (
    <Tooltip title={name} placement="bottom">
      <Box
        ref={ref}
        onClick={onEdit}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onEdit();
          }
        }}
        sx={{
          display: "inline-block",
          position: "relative",
          cursor: "pointer",
          padding: "2px 4px",
          maxWidth: 600,
          transition: "all 0.2s ease-in-out",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 4,
            right: 4,
            height: "2px",
            backgroundColor: "text.secondary",
            opacity: 0,
            transform: "scaleX(0)",
            transformOrigin: "center",
            transition: "transform 0.2s ease-in-out, opacity 0.2s ease-in-out",
          },
          "&:hover": {
            "&::after": {
              opacity: 0.3,
              transform: "scaleX(1)",
            },
          },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Typography>
      </Box>
    </Tooltip>
  );
});

DisplayTitle.displayName = "DisplayTitle";

export const EditableIntegrationTitle: React.FC = () => {
  const { integration } = useSalesforceIntegrationDetailsContext();
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(integration.name);
  const [initialWidth, setInitialWidth] = useState<number | undefined>(
    undefined
  );
  const displayRef = useRef<HTMLDivElement>(null);
  const { showInfo, showError } = useSnackbar();

  const [renameIntegrationMutation, { loading: isSaving }] = useMutation(
    RENAME_INTEGRATION_MUTATION,
    { context: { clientName: HG_FORCE_CLIENT } }
  );

  // Reset draft name when integration changes
  useEffect(() => {
    setDraftName(integration.name);
  }, [integration.name]);

  // Validation logic
  const validation = useMemo(() => {
    const trimmed = draftName.trim();
    const nonWhitespaceLength = trimmed.replace(/\s/g, "").length;

    return {
      isValid:
        trimmed.length > 0 &&
        nonWhitespaceLength >=
          EDITABLE_INTEGRATION_TITLE_TEXTS.validation.minNonWhitespaceChars,
      isEmpty: trimmed.length === 0,
      isTooShort:
        nonWhitespaceLength <
        EDITABLE_INTEGRATION_TITLE_TEXTS.validation.minNonWhitespaceChars,
      hasChanges: trimmed !== integration.name,
    };
  }, [draftName, integration.name]);

  const handleSave = useCallback(async () => {
    const trimmed = draftName.trim();

    if (!validation.isValid) {
      // Reset to original if invalid
      setDraftName(integration.name);
      setIsEditing(false);
      return;
    }

    if (validation.hasChanges) {
      try {
        setDraftName(trimmed);
        await renameIntegrationMutation({
          variables: { integrationId: integration.id, name: trimmed },
        });
        showInfo(EDITABLE_INTEGRATION_TITLE_TEXTS.messages.renameSuccess);
        setIsEditing(false);
      } catch (error) {
        showError(EDITABLE_INTEGRATION_TITLE_TEXTS.messages.renameError);
        setDraftName(integration.name);
        setIsEditing(false);
        throw error;
      }
    } else {
      setIsEditing(false);
    }
  }, [
    draftName,
    validation.isValid,
    validation.hasChanges,
    integration.name,
    integration.id,
    renameIntegrationMutation,
    showInfo,
    showError,
  ]);

  const handleCancel = useCallback(() => {
    setDraftName(integration.name);
    setIsEditing(false);
  }, [integration.name]);

  const handleStartEditing = useCallback(() => {
    // Capture the current width of the display title
    if (displayRef.current) {
      const currentWidth = displayRef.current.offsetWidth;
      setInitialWidth(currentWidth);
    }
    setIsEditing(true);
  }, []);

  const handleChange = useCallback((value: string) => {
    setDraftName(value);
  }, []);

  if (isEditing) {
    return (
      <EditingTitle
        value={draftName}
        onChange={handleChange}
        onSave={handleSave}
        onCancel={handleCancel}
        disabled={isSaving}
        initialWidth={initialWidth}
        validation={validation}
      />
    );
  }

  return (
    <DisplayTitle
      ref={displayRef}
      name={integration.name}
      onEdit={handleStartEditing}
    />
  );
};
